/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable keyword-spacing */
import { ChangeDetectorRef, Directive, ElementRef, HostBinding, OnDestroy, OnInit, inject, input } from '@angular/core';
import { NgControl } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';
import { InputMask } from 'primeng/inputmask';
import { Subscription } from 'rxjs';

import { showValidationFailure } from '../utils/validation.util';

/**
 * cmg-form-control-error - A directive to apply (red) validation error styling on FormControl.
 * Currently supporting \<input>, \<p-inputMask>, and \<p-datepicker>.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[cmg-input-error]',
})
export class CmgInputErrorDirective implements OnInit, OnDestroy {
  private readonly _el = inject(ElementRef);
  private readonly _control = inject(NgControl);
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _inputMask = inject(InputMask, { optional: true });
  private readonly _calendar = inject(DatePicker, { optional: true });

  private _errorEventSub: Subscription;
  private _statusChangesSub: Subscription;

  /** The type of UI control receiving error styling. */
  readonly errorControl = input<'input' | 'p-inputMask' | 'p-datepicker'>(undefined);

  /** The event to trigger the error styling.
   *
   * [Note]: 'input' only supports blur events at this time.
   */
  readonly errorEvent = input<string>(undefined);

  /** Determines whether to apply the 'input-error' CSS class - \styles\components\_input.scss. */
  @HostBinding('class.input-error') errorClass = false;

  ngOnInit() {
    // Two subscriptions because error styling can be triggered two ways:

    // (1) Events (e.g. blur)
    this._errorEventSub = this._assembleErrorTrigger()?.subscribe(this._updateErrorStyling);

    // (2) Status changes (e.g. untouched --> touched).
    this._statusChangesSub = this._control.statusChanges.subscribe(this._updateErrorStyling);
  }

  ngOnDestroy() {
    [this._errorEventSub, this._statusChangesSub].forEach((sub) => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  /** Matches the error control type with the event to trigger error styling. */
  private _assembleErrorTrigger() {
    switch (this.errorControl()) {
      case 'input':
        this._el.nativeElement.addEventListener(this.errorEvent(), this.onBlur.bind(this));
        return null;

      case 'p-inputMask':
        return this._inputMask[this.errorEvent()];

      case 'p-datepicker':
        return this._calendar[this.errorEvent()];
    }
  }

  /** For \<input> only. */
  onBlur() {
    this._updateErrorStyling();
  }

  /** Toggles whether to apply .input-error CSS class to host element. */
  private readonly _updateErrorStyling = () => {
    if (this._control) {
      this.errorClass = showValidationFailure(this._control);

      // This ensures red error styling is applied.
      this._cdRef.detectChanges();
    }
  };
}
